import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, interval, map, Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AbstractApiService } from '../abstract-api-service/abstract-api-service';
import { AppConstants } from 'src/app/constants/app-constants';
import { UserService } from '../user/user.service';
import { User } from 'src/app/model/User';
import { AuthService } from '../auth/auth.service';
import { AuthorizationService } from '../authorization/authorization.service';
import { NLSTransaction, NLSTransactionInterface, NLSTransactionMapper } from 'src/app/model/NLSTransaction';
import { TransactionDetail, TransactionDetailInterface, TransactionDetailMapper } from 'src/app/model/TransactionDetail';
import { NLS_TRANSACTIONS_ENDPOINT, TRANSACTION_DETAILS_ENDPOINT, TRANSACTION_DETAILS_PENDING_PAYMENTS_ENDPOINT } from 'src/app/constants/api-routes';

/** Data object provided to refund endpoint */
export interface RefundData {
    amount: number;
}

/** Api return types */
export type RefundReturnType = {loan_number: string, refund_amount: number, status: string, transaction_code: string};


/**
 * NLSTransactionService handles data operations with NLS Transactions
 */
@Injectable({
  providedIn: 'root'
})
export class NLSTransactionService extends AbstractApiService<NLSTransaction> {
  /** Dependency injections */
  private _authorizationService: AuthorizationService = inject(AuthorizationService);

  /** Observable that emits the latest list of nls transactions */
  protected _subject = new Subject<NLSTransaction[]>();
  public changes$ = this._subject.asObservable();

  /** A list of 'nls transactions' the user has access to */
  protected _collection?: NLSTransaction[];

  /** An observable to refresh API data */
  public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
  private _timers: number[] = [0];

  /** How long it has been since the last refresh */
  private _timeSinceLastRefresh = new BehaviorSubject<string>("0:00");
  public timeSinceLastRefresh$ = this._timeSinceLastRefresh.asObservable();

  /**
   * Default constructor for the service, used to initialize services
   *
   * @param _userService
   * @param _authService
   * @param _http
   */
  constructor(
    private _userService: UserService,
    private _authService: AuthService,
    protected _http: HttpClient,
  ) {
    super();

    // On authentication changes, clear persisted data
    this._userService.user$.subscribe((user: User | null) => {
      if (this._authService.authenticated && user != undefined) {
        this._fetch();
      } else {
        this._collection = undefined;
        this._subject.next([]);
      }
    });

    // Continuously query the backend to receive new nls transactions
    this._startTimer(0);
    this.timer.subscribe(
      (x: number) => {
        this._timers.splice(this._timers.indexOf(x));
        this._startTimer(x + 1);
        this._fetch();
      });
  }

  /**
   * Get a list of nls transactions
   *
   * @return A reference to the current collection
   */
  public getEntities(): NLSTransaction[] | undefined {
    return this._collection?.map(x => x.copyOf());
  }

  /**
   * Get a single nls transaction by ID
   *
   * @param id The transaction ID to find
   * @return A nls transaction or undefined if not found
   */
  public getById(id: number): NLSTransaction | undefined {
    return this._collection?.find(t => t.transaction_id === id)?.copyOf();
  }

  /**
   * Fetch pending payments for a given transaction
   * 
   * @param id The ID of the transaction to fetch pending payments for
   * @returns Observable of pending payments
   */
  public fetchPendingPayments(id: number): Observable<TransactionDetail[]> {
    // Make sure transaction event exists
    const transaction = this._collection?.find(t => t.transaction_id === id);
    if (!transaction) {
      throw new Error(`Transaction with ID '${id}' not found`);
    }

    // Get the loan number
    const loanNumber = transaction.loan_number;
    if (!loanNumber) {
      throw new Error(`Transaction with ID '${id}' does not have a valid loan number`);
    }

    const companyId = this._userService.company?.companyId;
    if (!companyId) {
      throw new Error('Company information is missing');
    }

    // Perform http request
    const url = TRANSACTION_DETAILS_PENDING_PAYMENTS_ENDPOINT(companyId, loanNumber);
    return this._http.get<TransactionDetailInterface[]>(url).pipe(
      map((dtos: TransactionDetailInterface[]) => dtos.map(dto => TransactionDetailMapper.toModel(dto)))
    );
  }

  /**
   * Fetch all transaction details for a given transaction. This does not fetch pending payments
   * 
   * @param id The ID of the transaction to fetch details for
   * @return Observable of transaction details
   */
  public fetchTransactionDetails(id: number): Observable<TransactionDetail[]> {
    // Make sure transaction event exists
    const transaction = this._collection?.find(t => t.transaction_id === id);
    if (!transaction) {
      throw new Error(`Transaction with ID '${id}' not found`);
    }

    // Find the loan number
    const loanNumber = transaction.loan_number;
    if (!loanNumber) {
      throw new Error(`Transaction with ID '${id}' does not have a valid loan number`);
    }

    const companyId = this._userService.company?.companyId;
    if (!companyId) {
      throw new Error('Company information is missing');
    }

    // Perform http request
    return this._http.get<TransactionDetailInterface[]>(TRANSACTION_DETAILS_ENDPOINT(companyId, loanNumber)).pipe(
      map((dtos: TransactionDetailInterface[]) => dtos.map(dto => TransactionDetailMapper.toModel(dto)))
    );
  }

  /** Helper methods */

  /**
   * Set a list of nls transactions to the current collection
   *
   * @param nlsTransactions
   */
  private _setEntities(nlsTransactions: NLSTransaction[]): void {
    this._collection = nlsTransactions.map(x => x.copyOf());
    this._subject.next(nlsTransactions.map(x => x.copyOf()));
  }

  /**
   * Get nls transactions from the backend. You should subscribe to the 'changes$'
   * field to receive updates
   */
  private _fetch(): void {
    // Make sure the user is authenticated
    if (!this._authService.authenticated) return;

    // Get the user's active company
    const company = this._userService.company;
    if (company == undefined) return;

    // Get role and company id
    const role = company.companyType?.toLowerCase() as 'borrower' | 'supplier';
    const companyId = company.companyId;
    if (role == undefined || companyId == undefined) return;

    this._http.get<NLSTransactionInterface[]>(NLS_TRANSACTIONS_ENDPOINT(companyId, role)).subscribe({
      next: (nlsTransactionInterfaces: NLSTransactionInterface[]): void => {
        if (nlsTransactionInterfaces == undefined) {
          console.error(`[ERR]: cannot cast backend output into nls transaction list`);
        }

        // Convert the interfaces to class values
        let nlsTransactions: NLSTransaction[] = [];
        for (let nlsTransactionInt of nlsTransactionInterfaces) {
          nlsTransactions.push(NLSTransactionMapper.toModel(nlsTransactionInt));
        }

        // Propagate changes if different
        this._setEntities(nlsTransactions);
        console.log(`[INFO]: successfully queried nls transactions`);
      },
      error: (error) => {
        console.error(`[ERR]: failed to fetch nls transactions`, error);
      }
    });
  }

  /**
   * Start the last updated time from 0
   */
  private async _startTimer(index: number) {
    this._timers.push(index);

    let seconds = 0;
    let minutes = 0;

    while (this._timers.includes(index)) {
      // Increment seconds
      await delay(1000);
      seconds++;

      // Increment minutes
      if (seconds >= 60) {
        seconds = 0;
        minutes++;
      }

      // Update timer
      this._timeSinceLastRefresh.next(`${minutes}:${("" + seconds).length === 1 ? "0" : ""}${seconds}`);
    }
  }
}

// Helper function to delay for a given number of milliseconds
const delay = (delay: number): Promise<void> => {
  return new Promise(r => { setTimeout(r, delay) });
}